/**
 * Access Matrix — Admin view for managing cross-tier club access rules.
 *
 * Displays a matrix where rows = home tiers and columns = destination tiers.
 * Each cell shows whether a member at the home tier can access clubs belonging
 * to the destination tier. Click a cell to toggle access and edit the reason.
 */

import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  IconButton,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import type { SelectChangeEvent } from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import BlockIcon from '@mui/icons-material/Block';
import DeleteIcon from '@mui/icons-material/Delete';
import DownloadIcon from '@mui/icons-material/Download';
import {
  getTiers,
  getAccessRules,
  saveAccessRule,
  deleteAccessRule,
  getRegions,
} from '@/services/adminApi';
import type { TierRecord, AccessRule } from '@/types';

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function accessKey(homeTierId: string, destTierId: string): string {
  return `${homeTierId}|${destTierId}`;
}

// ---------------------------------------------------------------------------
// Cell component (memoized)
// ---------------------------------------------------------------------------

interface MatrixCellProps {
  rule: AccessRule | undefined;
  isDiagonal: boolean;
  homeLabel: string;
  destLabel: string;
  onClick: () => void;
}

const MatrixCell = React.memo(function MatrixCell({
  rule,
  isDiagonal,
  homeLabel,
  destLabel,
  onClick,
}: MatrixCellProps) {
  if (isDiagonal) {
    return (
      <TableCell
        sx={{
          backgroundColor: '#424242',
          minWidth: 70,
          p: 0.5,
          cursor: 'default',
        }}
      />
    );
  }

  const hasAccess = rule?.hasAccess ?? false;
  const reason = rule?.reason ?? '';

  if (hasAccess) {
    return (
      <Tooltip
        title={`${homeLabel} can access ${destLabel}${reason ? ` — ${reason}` : ''}`}
        arrow
      >
        <TableCell
          onClick={onClick}
          sx={{
            backgroundColor: '#e8f5e9',
            minWidth: 70,
            p: 0.5,
            cursor: 'pointer',
            textAlign: 'center',
            '&:hover': { backgroundColor: '#c8e6c9' },
          }}
        >
          <CheckCircleIcon sx={{ color: '#2e7d32', fontSize: 20 }} />
          {reason && (
            <Typography
              variant="caption"
              sx={{ display: 'block', fontSize: '0.6rem', color: '#2e7d32', lineHeight: 1.2 }}
            >
              {reason}
            </Typography>
          )}
        </TableCell>
      </Tooltip>
    );
  }

  return (
    <Tooltip title={`${homeLabel} cannot access ${destLabel} (no rule)`} arrow>
      <TableCell
        onClick={onClick}
        sx={{
          backgroundColor: '#f5f5f5',
          minWidth: 70,
          p: 0.5,
          cursor: 'pointer',
          textAlign: 'center',
          '&:hover': { backgroundColor: '#eeeeee' },
        }}
      >
        <BlockIcon sx={{ color: '#bdbdbd', fontSize: 18 }} />
      </TableCell>
    </Tooltip>
  );
});

// ---------------------------------------------------------------------------
// Edit dialog
// ---------------------------------------------------------------------------

interface EditDialogProps {
  open: boolean;
  homeLabel: string;
  destLabel: string;
  hasAccess: boolean;
  reason: string;
  isNew: boolean;
  saving: boolean;
  onAccessChange: (v: boolean) => void;
  onReasonChange: (v: string) => void;
  onSave: () => void;
  onDelete: () => void;
  onCancel: () => void;
}

function EditDialog({
  open,
  homeLabel,
  destLabel,
  hasAccess,
  reason,
  isNew,
  saving,
  onAccessChange,
  onReasonChange,
  onSave,
  onDelete,
  onCancel,
}: EditDialogProps) {
  return (
    <Dialog open={open} onClose={onCancel} maxWidth="sm" fullWidth>
      <DialogTitle>
        {isNew ? 'Create Access Rule' : 'Edit Access Rule'}
      </DialogTitle>
      <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 2 }}>
        <Box sx={{ p: 1.5, bgcolor: '#f5f5f5', borderRadius: 1 }}>
          <Typography variant="body2">
            <strong>Home Tier:</strong> {homeLabel}
          </Typography>
          <Typography variant="body2">
            <strong>Destination Tier:</strong> {destLabel}
          </Typography>
        </Box>
        <FormControl size="small" fullWidth>
          <InputLabel>Access</InputLabel>
          <Select
            value={hasAccess ? 'yes' : 'no'}
            label="Access"
            onChange={(e) => onAccessChange(e.target.value === 'yes')}
          >
            <MenuItem value="yes">Yes — allowed</MenuItem>
            <MenuItem value="no">No — blocked</MenuItem>
          </Select>
        </FormControl>
        <TextField
          label="Reason (optional)"
          helperText="Explain why access is granted or blocked"
          value={reason}
          onChange={(e) => onReasonChange(e.target.value)}
          size="small"
          fullWidth
          multiline
          minRows={2}
        />
      </DialogContent>
      <DialogActions sx={{ justifyContent: 'space-between', px: 3, pb: 2 }}>
        <Box>
          {!isNew && (
            <Tooltip title="Delete this access rule">
              <IconButton color="error" onClick={onDelete} disabled={saving}>
                <DeleteIcon />
              </IconButton>
            </Tooltip>
          )}
        </Box>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button onClick={onCancel} disabled={saving}>
            Cancel
          </Button>
          <Button variant="contained" onClick={onSave} disabled={saving}>
            {saving ? <CircularProgress size={20} /> : 'Save'}
          </Button>
        </Box>
      </DialogActions>
    </Dialog>
  );
}

// ---------------------------------------------------------------------------
// Main component
// ---------------------------------------------------------------------------

export default function AccessMatrix() {
  // Region selection
  const [homeRegion, setHomeRegion] = useState<string>('DMV');
  const [destRegion, setDestRegion] = useState<string>('DMV');

  // Data
  const [homeTiers, setHomeTiers] = useState<TierRecord[]>([]);
  const [destTiers, setDestTiers] = useState<TierRecord[]>([]);
  const [rules, setRules] = useState<AccessRule[]>([]);

  // Available regions
  const [regions, setRegions] = useState<string[]>(['DMV']);

  // Loading / error
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Edit dialog state
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editHomeTier, setEditHomeTier] = useState<TierRecord | null>(null);
  const [editDestTier, setEditDestTier] = useState<TierRecord | null>(null);
  const [editHasAccess, setEditHasAccess] = useState(false);
  const [editReason, setEditReason] = useState('');
  const [editIsNew, setEditIsNew] = useState(false);
  const [saving, setSaving] = useState(false);

  // ---------------------------------------------------------------------------
  // Load data
  // ---------------------------------------------------------------------------

  const loadData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [hTiers, dTiers, allRules, regionList] = await Promise.all([
        getTiers(homeRegion),
        homeRegion === destRegion ? getTiers(homeRegion) : getTiers(destRegion),
        getAccessRules(homeRegion),
        getRegions(),
      ]);
      if (regionList.length > 0) setRegions(regionList);
      setHomeTiers([...hTiers].sort((a, b) => a.rank - b.rank));
      setDestTiers(
        homeRegion === destRegion
          ? [...hTiers].sort((a, b) => a.rank - b.rank)
          : [...dTiers].sort((a, b) => a.rank - b.rank),
      );
      // Filter to only rules targeting the selected destination region
      const filtered = allRules.filter((r) => r.destRegion === destRegion);
      setRules(filtered);
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to load data';
      setError(message);
      console.error('AccessMatrix: failed to load data', err);
    } finally {
      setLoading(false);
    }
  }, [homeRegion, destRegion]);

  useEffect(() => {
    void loadData();
  }, [loadData]);

  // ---------------------------------------------------------------------------
  // Derived data
  // ---------------------------------------------------------------------------

  const ruleMap = useMemo(() => {
    const map = new Map<string, AccessRule>();
    for (const r of rules) {
      map.set(accessKey(r.homeTierId, r.destTierId), r);
    }
    return map;
  }, [rules]);

  // ---------------------------------------------------------------------------
  // Cell click handler
  // ---------------------------------------------------------------------------

  const handleCellClick = useCallback(
    (home: TierRecord, dest: TierRecord) => {
      const existing = ruleMap.get(accessKey(home.tierId, dest.tierId));
      setEditHomeTier(home);
      setEditDestTier(dest);
      if (existing) {
        setEditHasAccess(existing.hasAccess);
        setEditReason(existing.reason || '');
        setEditIsNew(false);
      } else {
        setEditHasAccess(true);
        setEditReason('');
        setEditIsNew(true);
      }
      setDialogOpen(true);
    },
    [ruleMap],
  );

  // ---------------------------------------------------------------------------
  // Save handler
  // ---------------------------------------------------------------------------

  const handleSave = useCallback(async () => {
    if (!editHomeTier || !editDestTier) return;
    setSaving(true);
    try {
      const rule: AccessRule = {
        homeTierId: editHomeTier.tierId,
        homeRegion,
        destTierId: editDestTier.tierId,
        destRegion,
        hasAccess: editHasAccess,
        reason: editReason.trim(),
      };
      await saveAccessRule(rule);
      // Update local state
      setRules((prev) => {
        const key = accessKey(rule.homeTierId, rule.destTierId);
        const idx = prev.findIndex(
          (r) => accessKey(r.homeTierId, r.destTierId) === key,
        );
        if (idx >= 0) {
          const next = [...prev];
          next[idx] = rule;
          return next;
        }
        return [...prev, rule];
      });
      setDialogOpen(false);
    } catch (err) {
      console.error('AccessMatrix: failed to save rule', err);
      setError(err instanceof Error ? err.message : 'Failed to save rule');
    } finally {
      setSaving(false);
    }
  }, [editHomeTier, editDestTier, editHasAccess, editReason, homeRegion, destRegion]);

  // ---------------------------------------------------------------------------
  // Delete handler
  // ---------------------------------------------------------------------------

  const handleDelete = useCallback(async () => {
    if (!editHomeTier || !editDestTier) return;
    setSaving(true);
    try {
      await deleteAccessRule(
        homeRegion,
        editHomeTier.tierId,
        destRegion,
        editDestTier.tierId,
      );
      setRules((prev) =>
        prev.filter(
          (r) =>
            accessKey(r.homeTierId, r.destTierId) !==
            accessKey(editHomeTier.tierId, editDestTier.tierId),
        ),
      );
      setDialogOpen(false);
    } catch (err) {
      console.error('AccessMatrix: failed to delete rule', err);
      setError(err instanceof Error ? err.message : 'Failed to delete rule');
    } finally {
      setSaving(false);
    }
  }, [editHomeTier, editDestTier, homeRegion, destRegion]);

  // ---------------------------------------------------------------------------
  // Region change handlers
  // ---------------------------------------------------------------------------

  const handleHomeChange = useCallback((e: SelectChangeEvent) => {
    const region = e.target.value;
    setHomeRegion(region);
    setDestRegion(region);
  }, []);

  const handleDestChange = useCallback((e: SelectChangeEvent) => {
    setDestRegion(e.target.value);
  }, []);

  // ---------------------------------------------------------------------------
  // Export to CSV
  // ---------------------------------------------------------------------------

  const handleExportCsv = useCallback(() => {
    const escCsv = (s: string) => `"${s.replace(/"/g, '""')}"`;
    const header = ['Home \\ Dest', ...destTiers.map(t => t.displayName)];
    const rows = homeTiers.map(row => {
      const cells = destTiers.map(col => {
        const isDiag = homeRegion === destRegion && row.tierId === col.tierId;
        if (isDiag) return '—';
        const rule = ruleMap.get(accessKey(row.tierId, col.tierId));
        if (rule && rule.hasAccess) return rule.reason ? `Yes (${rule.reason})` : 'Yes';
        return 'No';
      });
      return [row.displayName, ...cells];
    });
    const csv = [header, ...rows].map(r => r.map(escCsv).join(',')).join('\n');
    const blob = new Blob(['\ufeff' + csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `access-matrix-${homeRegion}-to-${destRegion}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  }, [homeTiers, destTiers, ruleMap, homeRegion, destRegion]);

  // ---------------------------------------------------------------------------
  // Render
  // ---------------------------------------------------------------------------

  return (
    <Box sx={{ p: 3, maxWidth: '100%' }}>
      {/* Header */}
      <Typography variant="h4" gutterBottom>
        Access Matrix
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        Defines which home tiers can access clubs belonging to destination tiers.
        Click any cell to toggle access or edit the reason.
      </Typography>

      {/* Region pickers */}
      <Box sx={{ display: 'flex', gap: 2, mb: 3, flexWrap: 'wrap' }}>
        <FormControl size="small" sx={{ minWidth: 160 }}>
          <InputLabel>Home Region</InputLabel>
          <Select
            value={homeRegion}
            label="Home Region"
            onChange={handleHomeChange}
          >
            {regions.map((r) => (
              <MenuItem key={r} value={r}>
                {r}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        <FormControl size="small" sx={{ minWidth: 160 }}>
          <InputLabel>Destination Region</InputLabel>
          <Select
            value={destRegion}
            label="Destination Region"
            onChange={handleDestChange}
          >
            {regions.map((r) => (
              <MenuItem key={r} value={r}>
                {r}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        <Button
          variant="outlined"
          startIcon={<DownloadIcon />}
          onClick={handleExportCsv}
          disabled={loading || homeTiers.length === 0}
          size="small"
        >
          Export CSV
        </Button>
      </Box>

      {/* Error */}
      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {/* Loading */}
      {loading && (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
          <CircularProgress />
        </Box>
      )}

      {/* Matrix table */}
      {!loading && homeTiers.length > 0 && destTiers.length > 0 && (
        <TableContainer
          component={Paper}
          sx={{
            maxHeight: 'calc(100vh - 320px)',
            overflow: 'auto',
            mb: 3,
          }}
        >
          <Table
            stickyHeader
            size="small"
            sx={{ minWidth: destTiers.length * 80 + 200 }}
          >
            <TableHead>
              <TableRow>
                {/* Top-left corner */}
                <TableCell
                  sx={{
                    position: 'sticky',
                    left: 0,
                    zIndex: 3,
                    backgroundColor: '#fafafa',
                    fontWeight: 'bold',
                    minWidth: 180,
                    borderRight: '2px solid #e0e0e0',
                  }}
                >
                  Home \ Destination
                </TableCell>
                {destTiers.map((col) => (
                  <TableCell
                    key={col.tierId}
                    sx={{
                      fontWeight: 'bold',
                      fontSize: '0.7rem',
                      textAlign: 'left',
                      p: 0.5,
                      minWidth: 70,
                      maxWidth: 90,
                      height: 120,
                      verticalAlign: 'bottom',
                      backgroundColor: '#fafafa',
                      overflow: 'hidden',
                    }}
                  >
                    <Tooltip title={col.displayName} arrow>
                      <Box
                        sx={{
                          transform: 'rotate(-55deg)',
                          transformOrigin: 'bottom left',
                          whiteSpace: 'nowrap',
                          ml: 2.5,
                          mb: 0.5,
                        }}
                      >
                        {col.displayName}
                      </Box>
                    </Tooltip>
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {homeTiers.map((row) => (
                <TableRow key={row.tierId} hover={false}>
                  {/* Sticky first column */}
                  <TableCell
                    sx={{
                      position: 'sticky',
                      left: 0,
                      zIndex: 1,
                      backgroundColor: '#fafafa',
                      fontWeight: 'bold',
                      fontSize: '0.8rem',
                      whiteSpace: 'nowrap',
                      borderRight: '2px solid #e0e0e0',
                      p: 1,
                    }}
                  >
                    {row.displayName}
                  </TableCell>
                  {destTiers.map((col) => {
                    const isDiagonal =
                      homeRegion === destRegion && row.tierId === col.tierId;
                    const rule = ruleMap.get(accessKey(row.tierId, col.tierId));
                    return (
                      <MatrixCell
                        key={col.tierId}
                        rule={rule}
                        isDiagonal={isDiagonal}
                        homeLabel={row.displayName}
                        destLabel={col.displayName}
                        onClick={() => {
                          if (!isDiagonal) handleCellClick(row, col);
                        }}
                      />
                    );
                  })}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {/* Empty state */}
      {!loading && (homeTiers.length === 0 || destTiers.length === 0) && !error && (
        <Alert severity="info" sx={{ mb: 2 }}>
          No tiers found for the selected region(s). Make sure tier data has been
          loaded into DynamoDB.
        </Alert>
      )}

      {/* Legend */}
      <Paper sx={{ p: 2, mt: 1 }}>
        <Typography variant="subtitle2" gutterBottom>
          Legend
        </Typography>
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, alignItems: 'center' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <CheckCircleIcon sx={{ color: '#2e7d32', fontSize: 18 }} />
            <Typography variant="caption">Access allowed</Typography>
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <BlockIcon sx={{ color: '#bdbdbd', fontSize: 18 }} />
            <Typography variant="caption">No access (no rule)</Typography>
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <Box
              sx={{
                width: 16,
                height: 16,
                backgroundColor: '#424242',
                border: '1px solid #ccc',
                borderRadius: 0.5,
              }}
            />
            <Typography variant="caption">Same tier (diagonal)</Typography>
          </Box>
        </Box>
        <Typography
          variant="caption"
          color="text.secondary"
          sx={{ display: 'block', mt: 1 }}
        >
          Click any cell to create, edit, or delete an access rule. Rules define
          whether a member&apos;s home tier grants access to clubs belonging to
          the destination tier.
        </Typography>
      </Paper>

      {/* Edit dialog */}
      <EditDialog
        open={dialogOpen}
        homeLabel={editHomeTier?.displayName ?? ''}
        destLabel={editDestTier?.displayName ?? ''}
        hasAccess={editHasAccess}
        reason={editReason}
        isNew={editIsNew}
        saving={saving}
        onAccessChange={setEditHasAccess}
        onReasonChange={setEditReason}
        onSave={() => void handleSave()}
        onDelete={() => void handleDelete()}
        onCancel={() => setDialogOpen(false)}
      />
    </Box>
  );
}
